import React, { useEffect, useState } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from '../../hooks/useNavigate';
import { SvgTime } from '../../icons/v1/Time';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { Button } from './Button';
import { Text } from './Text';
import { View } from './View';

export function CountdownBanner() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const { isNarrowWidth } = useResponsive();
  const navigate = useNavigate();
  const { billingStatus } = useAuth();

  const handleClose = () => {
    setIsHidden(true);
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!billingStatus?.trialEnds) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsLoading(false);
        return;
      }

      const now = new Date();
      const trialEndDate = new Date(billingStatus.trialEnds);

      const timeDiff = trialEndDate.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      }

      setIsLoading(false);
    };

    calculateTimeLeft();
  }, [billingStatus]);

  useEffect(() => {
    if (isLoading) return;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        const totalSeconds = prevTime.days * 24 * 60 * 60 + 
                           prevTime.hours * 60 * 60 + 
                           prevTime.minutes * 60 + 
                           prevTime.seconds - 1;
        
        if (totalSeconds <= 0) {
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        const days = Math.floor(totalSeconds / (24 * 60 * 60));
        const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
        const seconds = totalSeconds % 60;

        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isLoading]);

  if (isLoading || isHidden || !billingStatus?.isActive || !billingStatus?.isFreeTrialProduct) return null;

  return (
    <View
      style={{
        backgroundColor: '#9DC88D',
        borderBottom: `1px solid ${theme.noticeBorder}`,
        padding: isNarrowWidth ? '10px 12px' : '12px 20px 12px 20px',
        paddingRight: isNarrowWidth ? 12 : 40,
        flexDirection: isNarrowWidth ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isNarrowWidth ? 8 : 12,
        position: 'relative',
      }}
    >
      {/* Close Button */}
      <View
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 10,
        }}
      >
        <Button
          type="bare"
          style={{
            padding: '2px',
            minWidth: 'auto',
            minHeight: 'auto',
            width: isNarrowWidth ? 18 : 20,
            height: isNarrowWidth ? 18 : 20,
            borderRadius: '50%',
            backgroundColor: 'rgba(45, 74, 45, 0.2)',
            border: '1px solid rgba(45, 74, 45, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
          onClick={handleClose}
        >
          <Text
            style={{
              fontSize: isNarrowWidth ? 10 : 12,
              fontWeight: 'bold',
              color: '#2D4A2D',
              lineHeight: 1,
            }}
          >
            ×
          </Text>
        </Button>
      </View>

      {/* Timer Section */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          width: isNarrowWidth ? '100%' : undefined,
          justifyContent: isNarrowWidth ? 'center' : 'flex-start',
          zIndex: 1,
        }}
      >
        <SvgTime
          style={{ 
            width: isNarrowWidth ? 12 : 16, 
            height: isNarrowWidth ? 12 : 16, 
            color: '#2D4A2D'
          }}
        />
        <Text
          style={{
            fontSize: isNarrowWidth ? 11 : 14,
            fontWeight: 600,
            color: '#2D4A2D',
          }}
        >
          {(() => {
            if (timeLeft.days > 1) {
              return isNarrowWidth
                ? `${timeLeft.days}d`
                : `${timeLeft.days} day${timeLeft.days !== 1 ? 's' : ''}`;
            } else if (timeLeft.days === 1 || timeLeft.hours > 0) {
              if (isNarrowWidth) {
                return timeLeft.days === 1
                  ? `${timeLeft.hours}h ${timeLeft.minutes}m`
                  : `${timeLeft.hours}h`;
              } else {
                return timeLeft.days === 1
                  ? `${timeLeft.hours} hour${timeLeft.hours !== 1 ? 's' : ''} ${timeLeft.minutes} minute${timeLeft.minutes !== 1 ? 's' : ''}`
                  : `${timeLeft.hours} hour${timeLeft.hours !== 1 ? 's' : ''}`;
              }
            } else if (timeLeft.minutes > 0) {
              return isNarrowWidth
                ? `${timeLeft.minutes}m`
                : `${timeLeft.minutes} minute${timeLeft.minutes !== 1 ? 's' : ''}`;
            } else {
              return isNarrowWidth
                ? `${timeLeft.seconds}s`
                : `${timeLeft.seconds} second${timeLeft.seconds !== 1 ? 's' : ''}`;
            }
          })()}
        </Text>
        {!isNarrowWidth && (
          <Text
            style={{
              fontSize: 13,
              color: '#2D4A2D',
              opacity: 0.8,
            }}
          >
            remaining
          </Text>
        )}
      </View>
      
      {/* Action Section */}
      <View
        style={{
          flexDirection: isNarrowWidth ? 'column' : 'row',
          alignItems: 'center',
          gap: isNarrowWidth ? 6 : 8,
          width: isNarrowWidth ? '100%' : undefined,
          justifyContent: isNarrowWidth ? 'center' : 'flex-end',
          zIndex: 1,
          marginRight: isNarrowWidth ? 0 : 20,
        }}
      >
        <Text
          style={{
            fontSize: isNarrowWidth ? 10 : 13,
            color: '#2D4A2D',
            opacity: 0.8,
            textAlign: isNarrowWidth ? 'center' : 'right',
            lineHeight: 1.3,
            width: isNarrowWidth ? '100%' : undefined,
          }}
        >
          {isNarrowWidth ? "Don't lose your data" : "Don't want to lose your data?"}
        </Text>
        <Button
          type="primary"
          style={{
            fontSize: isNarrowWidth ? 10 : 12,
            padding: isNarrowWidth ? '6px 12px' : '6px 12px',
            height: 'auto',
            minHeight: 'auto',
            borderRadius: isNarrowWidth ? 4 : 6,
            minWidth: 'auto',
          }}
          onClick={() => {
            navigate('/billing', { replace: false });
          }}
        >
          {isNarrowWidth ? 'Upgrade' : 'Upgrade Now'}
        </Button>
      </View>
    </View>
  );
}
